import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, BookOpen, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFoundingStatus } from "@/hooks/useFoundingStatus";
import FoundingBanner from "@/components/FoundingBanner";
import { COMMUNITY_FEATURES, PRO_FEATURES, PRO_PLANS, ANNUAL_SAVINGS } from "@/data/pricing";

// Public, reusable Free-vs-Pro pricing block with a monthly/annual toggle.
export default function PricingTiers({ defaultBilling = "monthly", showFounding = true }) {
  const { user } = useAuth();
  const founding = useFoundingStatus();
  const [billing, setBilling] = useState(defaultBilling);
  const plan = PRO_PLANS[billing];
  const proPrice = plan.price;
  const proSub = plan.sub;

  const isPaid = !!user?.subscription_active;
  const proTo = isPaid ? "/" : user ? "/subscribe" : "/register";
  const proCta = isPaid ? "You're on Pro" : user ? "Upgrade to Pro" : "Get Pro";

  return (
    <div data-testid="pricing-tiers">
      {/* Founding member urgency */}
      {showFounding && <FoundingBanner founding={founding} className="mb-6" />}

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => setBilling("monthly")}
          data-testid="pricing-billing-monthly"
          className={`h-10 px-5 rounded-full text-sm font-bold transition-colors ${billing === "monthly" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling("annual")}
          data-testid="pricing-billing-annual"
          className={`h-10 px-5 rounded-full text-sm font-bold transition-colors inline-flex items-center gap-2 ${billing === "annual" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          Annual <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">{ANNUAL_SAVINGS}</span>
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8 max-w-3xl mx-auto items-stretch">
        {/* Community (Free) */}
        <div className="rounded-2xl border border-slate-200 p-8 flex flex-col bg-white" data-testid="pricing-tier-community">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-xl font-bold text-slate-900">Community</h3>
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 bg-slate-100 rounded-full px-3 py-1">Free</span>
          </div>
          <div className="mt-3 flex items-end gap-1">
            <span className="font-heading text-5xl font-black tracking-tight text-slate-900">$0</span>
            <span className="text-slate-500 mb-1.5">/forever</span>
          </div>
          <p className="text-sm text-slate-500 mt-2">Everything you need to learn the business.</p>
          <ul className="space-y-3 mt-6 flex-1">
            {COMMUNITY_FEATURES.map((f) => (
              <li key={f} className="flex gap-3 text-slate-700 text-sm"><Check className="w-5 h-5 text-emerald-500 shrink-0" /> {f}</li>
            ))}
          </ul>
          <Link to={user ? "/#playbook" : "/register"} data-testid="pricing-community-cta">
            <Button variant="outline" className="w-full mt-6 h-12 rounded-full border-slate-300 font-bold"><BookOpen className="w-4 h-4 mr-2" /> {user ? "Go to your guide" : "Start free"}</Button>
          </Link>
        </div>

        {/* Pro (Paid) */}
        <div className="rounded-2xl border-2 border-brand ring-1 ring-brand/20 p-8 flex flex-col relative bg-orange-50/30" data-testid="pricing-tier-pro">
          <span className="absolute -top-3 left-8 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-brand rounded-full px-3 py-1">Most popular</span>
          <h3 className="font-heading text-xl font-bold text-slate-900">Pro</h3>
          <div className="mt-3 flex items-end gap-1">
            <span className="font-heading text-5xl font-black tracking-tight text-slate-900" data-testid="pricing-pro-price">{proPrice}</span>
            <span className="text-slate-500 mb-1.5">{plan.per}</span>
          </div>
          <p className="text-sm text-slate-500 mt-2" data-testid="pricing-pro-sub">{proSub}</p>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-6 mb-3">Everything in Community, plus:</div>
          <ul className="space-y-3 flex-1">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex gap-3 text-slate-700 text-sm"><Check className="w-5 h-5 text-emerald-500 shrink-0" /> {f}</li>
            ))}
          </ul>
          <Link to={proTo} data-testid="pricing-pro-cta" className={isPaid ? "pointer-events-none" : ""}>
            <Button disabled={isPaid} className="w-full mt-6 h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-bold group">
              {proCta} {!isPaid && <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />}
            </Button>
          </Link>
          <p className="text-xs text-center text-slate-400 mt-3">Cancel anytime. Secure checkout via Stripe.</p>
        </div>
      </div>
    </div>
  );
}
